import { FirebaseError } from 'firebase/app';
import { userFacingMessage, CLIENT_ERROR } from '@hotel-voice/shared';

/**
 * Turns anything thrown in the app into one short sentence a guest can act on.
 *
 * Cloud Functions send a stable code in `details.code`; that is what we switch
 * on. Raw Firebase or Agora messages are never rendered — they leak internals
 * and mean nothing to a hotel guest.
 */
export function toUserMessage(error: unknown): string {
  // The server's code wins over the transport status it travelled on. A
  // `FunctionsError` keeps `details` as an own property (see `errorCode`), and
  // NO_RECEPTIONIST_AVAILABLE ships with status `unavailable` — checking the
  // status first turned "nobody is at reception" into "you are offline".
  const code = errorCode(error);
  if (code) return userFacingMessage(code);

  if (error instanceof FirebaseError) {
    if (error.code === 'functions/unavailable' || error.code === 'auth/network-request-failed') {
      return userFacingMessage(CLIENT_ERROR.NETWORK_UNAVAILABLE);
    }
    // A callable that is not deployed (or is deployed to another region)
    // comes back 404. Saying "reception is unreachable" for that sends the
    // person debugging it to check the network instead of the deploy.
    if (error.code === 'functions/not-found') {
      return 'This feature is not available yet. Please try again later.';
    }
    if (error.code.startsWith('auth/')) {
      return userFacingMessage(CLIENT_ERROR.AUTH_FAILED);
    }
  }

  if (!navigator.onLine) {
    return userFacingMessage(CLIENT_ERROR.NETWORK_UNAVAILABLE);
  }

  return userFacingMessage(undefined);
}

/**
 * The machine-readable code, for branching. `undefined` when there isn't one.
 *
 * Looked for in two places because the SDK is inconsistent: a `FunctionsError`
 * carries `details` as an own property, while other `FirebaseError`s put it
 * under `customData`. Returning early on the `instanceof` alone missed every
 * callable error — `toUserMessage` above falls through to the second check
 * for the same reason.
 */
export function errorCode(error: unknown): string | undefined {
  if (error instanceof FirebaseError) {
    const details = error.customData?.['details'] as { code?: string } | undefined;
    if (details?.code) return details.code;
  }
  if (typeof error === 'object' && error !== null && 'details' in error) {
    return (error as { details?: { code?: string } }).details?.code;
  }
  return undefined;
}
